export const INTEGRATION_STATUS = Object.freeze({
  DISCONNECTED: 'disconnected',
  EXPIRED: 'expired',
  ACTIVE: 'active',
});

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const AUTHORIZATION_EXPIRATION_CLOCK_SKEW_MILLISECONDS = 5 * 60 * 1000;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const parseUtcTimestamp = (value) => {
  if (typeof value !== 'string') return Number.NaN;

  const normalizedValue = value.replace(
    /\.(\d+)(?=(?:z|[+-]\d{2}:\d{2})$)/i,
    (match, fraction) => `.${`${fraction}000`.slice(0, 3)}`,
  );
  return Date.parse(normalizedValue);
};

export const getExpirationTime = (account) => {
  if (!account || !account.refreshTokenExpiresAtUtc) return null;

  const expirationTime = parseUtcTimestamp(account.refreshTokenExpiresAtUtc);
  return Number.isFinite(expirationTime) ? expirationTime : null;
};

export const getRemainingDays = (account, now = Date.now()) => {
  const expirationTime = getExpirationTime(account);
  if (expirationTime !== null) {
    const remainingTime = expirationTime - now;
    if (remainingTime > 0 && remainingTime < DAY_IN_MILLISECONDS) return 0;
    return Math.ceil(remainingTime / DAY_IN_MILLISECONDS);
  }

  return toFiniteNumber(account && account.refreshTokenRemainingDays);
};

export const getIntegrationStatus = (account, now = Date.now()) => {
  if (!account || typeof account !== 'object') return INTEGRATION_STATUS.DISCONNECTED;

  const hasExpirationValue =
    account.refreshTokenExpiresAtUtc !== null && account.refreshTokenExpiresAtUtc !== undefined;
  const hasRemainingDaysValue =
    account.refreshTokenRemainingDays !== null && account.refreshTokenRemainingDays !== undefined;
  const remainingDays = getRemainingDays(account, now);
  const expirationTime = getExpirationTime(account);

  if (!hasExpirationValue && !hasRemainingDaysValue) {
    return INTEGRATION_STATUS.DISCONNECTED;
  }

  if (hasExpirationValue) {
    return expirationTime !== null && expirationTime > now ? INTEGRATION_STATUS.ACTIVE : INTEGRATION_STATUS.EXPIRED;
  }

  return remainingDays !== null && remainingDays > 0 ? INTEGRATION_STATUS.ACTIVE : INTEGRATION_STATUS.EXPIRED;
};

const statusOrder = {
  [INTEGRATION_STATUS.DISCONNECTED]: 0,
  [INTEGRATION_STATUS.EXPIRED]: 1,
  [INTEGRATION_STATUS.ACTIVE]: 2,
};

const compareText = (firstValue, secondValue) =>
  String(firstValue || '').localeCompare(String(secondValue || ''), 'tr', { sensitivity: 'base' });

const getExpirationSortValue = (account, now) => {
  const expirationTime = getExpirationTime(account);
  if (expirationTime !== null) return expirationTime;

  const remainingDays = getRemainingDays(account, now);
  return remainingDays === null ? Number.POSITIVE_INFINITY : now + remainingDays * DAY_IN_MILLISECONDS;
};

export const sortOAuthAccounts = (accounts, now = Date.now()) =>
  (Array.isArray(accounts) ? accounts : [])
    .filter((account) => account && typeof account === 'object')
    .map((account) => ({
      ...account,
      integrationStatus: getIntegrationStatus(account, now),
    }))
    .sort((firstAccount, secondAccount) => {
      const statusDifference =
        statusOrder[firstAccount.integrationStatus] - statusOrder[secondAccount.integrationStatus];
      if (statusDifference !== 0) return statusDifference;

      if (firstAccount.integrationStatus === INTEGRATION_STATUS.EXPIRED) {
        const firstExpiration = getExpirationTime(firstAccount) || Number.NEGATIVE_INFINITY;
        const secondExpiration = getExpirationTime(secondAccount) || Number.NEGATIVE_INFINITY;
        if (firstExpiration !== secondExpiration) return secondExpiration - firstExpiration;
      }

      if (firstAccount.integrationStatus === INTEGRATION_STATUS.ACTIVE) {
        const firstExpirationSortValue = getExpirationSortValue(firstAccount, now);
        const secondExpirationSortValue = getExpirationSortValue(secondAccount, now);
        if (firstExpirationSortValue !== secondExpirationSortValue) {
          return firstExpirationSortValue - secondExpirationSortValue;
        }
      }

      const bankNameDifference = compareText(firstAccount.bankName, secondAccount.bankName);
      if (bankNameDifference !== 0) return bankNameDifference;

      const bankCodeDifference = Number(firstAccount.bankCode || 0) - Number(secondAccount.bankCode || 0);
      return bankCodeDifference || compareText(firstAccount.uid, secondAccount.uid);
    });

export const groupOAuthAccountsByCompany = (accounts, now = Date.now()) => {
  const companies = (Array.isArray(accounts) ? accounts : [])
    .filter((account) => account && typeof account === 'object')
    .reduce((result, account) => {
      const companyIdentity = account.vkn || account.companyName || 'unknown-company';
      const companyKey = `company:${companyIdentity}`;

      if (!result[companyKey]) {
        result[companyKey] = {
          key: companyKey,
          companyName: account.companyName || '-',
          vkn: account.vkn || '-',
          accounts: [],
        };
      }

      result[companyKey].accounts.push(account);
      return result;
    }, {});

  return Object.keys(companies)
    .map((companyKey) => ({
      ...companies[companyKey],
      accounts: sortOAuthAccounts(companies[companyKey].accounts, now),
    }))
    .sort(
      (firstCompany, secondCompany) =>
        compareText(firstCompany.companyName, secondCompany.companyName) ||
        compareText(firstCompany.vkn, secondCompany.vkn),
    );
};

export const getIntegrationCounts = (accounts) =>
  (Array.isArray(accounts) ? accounts : []).reduce(
    (counts, account) => ({
      ...counts,
      [account.integrationStatus]: counts[account.integrationStatus] + 1,
    }),
    {
      [INTEGRATION_STATUS.DISCONNECTED]: 0,
      [INTEGRATION_STATUS.EXPIRED]: 0,
      [INTEGRATION_STATUS.ACTIVE]: 0,
    },
  );

export const getSafeAuthorizationUrl = (authorization, now = Date.now()) => {
  if (!authorization || typeof authorization.authorizationUrl !== 'string') {
    throw new Error('AUTHORIZATION_URL_MISSING');
  }

  const expirationTime = parseUtcTimestamp(authorization.expiresAtUtc);
  if (!Number.isFinite(expirationTime) || expirationTime <= now - AUTHORIZATION_EXPIRATION_CLOCK_SKEW_MILLISECONDS) {
    throw new Error('AUTHORIZATION_URL_EXPIRED');
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(authorization.authorizationUrl);
  } catch (error) {
    throw new Error('AUTHORIZATION_URL_INVALID');
  }

  if (parsedUrl.protocol !== 'https:' || !parsedUrl.hostname || parsedUrl.username || parsedUrl.password) {
    throw new Error('AUTHORIZATION_URL_UNSAFE');
  }

  return parsedUrl.toString();
};
